import { isEqual } from 'lodash-es';
import type { Action } from 'state/actions';
import type { DateFilter } from 'state/blotters/common/blotter.model';
import { toUtcDate, toUtcEndOfDay } from 'state/blotters/common/blotter.utils';
import { now } from 'utils/core/now';

import { onColumnStateChanged } from '../common/blotter.reducer.utils';
import type {
  SecondaryMarketTradeBlotterColumnDefinitionReceived,
  SecondaryMarketTradeBlotterFilterChanged,
  SecondaryMarketTradeBlotterFiltersLoaded,
  SecondaryMarketTradeBlotterItemsPerPageChanged,
  SecondaryMarketTradeBlotterItemsReceived,
  SecondaryMarketTradeBlotterPageChanged,
  SecondaryMarketTradeBlotterPreferenceStateLoaded,
} from './secondaryMarketTradeBlotter.action';
import type { SecondaryMarketTradeBlotterState } from './secondaryMarketTradeBlotter.model';
import { initialSecondaryMarketTradeBlotterState } from './secondaryMarketTradeBlotter.model';

// tslint:disable-next-line:cyclomatic-complexity
export function secondaryMarketTradeBlotterReducer(
  state: SecondaryMarketTradeBlotterState = initialSecondaryMarketTradeBlotterState,
  action: Action,
): SecondaryMarketTradeBlotterState {
  switch (action.type) {
    case 'SECONDARY_MARKET_TRADE_BLOTTER_COLUMN_DEFINITION_RECEIVED':
      return onColumnDefinitionReceived(state, action);
    case 'SECONDARY_MARKET_TRADE_BLOTTER_FILTERS_LOADED':
      return onFiltersLoaded(state, action);
    case 'SECONDARY_MARKET_TRADE_BLOTTER_PAGE_CHANGED':
      return onPageChanged(state, action);
    case 'SECONDARY_MARKET_TRADE_BLOTTER_ITEMS_PER_PAGE_CHANGED':
      return onItemsPerPageChanged(state, action);
    case 'SECONDARY_MARKET_TRADE_BLOTTER_FILTER_CHANGED':
      return onFilterChanged(state, action);
    case 'SECONDARY_MARKET_TRADE_BLOTTER_ADVANCED_FILTERS_TOGGLED':
      return onAdvancedFilterToggled(state);
    case 'SECONDARY_MARKET_TRADE_BLOTTER_ITEMS_RECEIVED':
      return onItemsReceived(state, action);
    case 'SECONDARY_MARKET_TRADE_BLOTTER_ITEMS_REQUESTED':
      return onItemsRequested(state);
    case 'SECONDARY_MARKET_TRADE_BLOTTER_PREFERENCE_STATE_GET':
      return { ...state, areBlotterPreferencesInError: false };
    case 'SECONDARY_MARKET_TRADE_BLOTTER_PREFERENCE_STATE_LOADED':
      return onBlotterPreferenceStateLoaded(state, action);
    case 'SECONDARY_MARKET_TRADE_BLOTTER_PREFERENCE_STATE_LOADING_FAILED':
      return { ...state, areBlotterPreferencesInError: true, canSaveBlotterPreferences: false };
    case 'SECONDARY_MARKET_TRADE_BLOTTER_COLUMN_STATE_CHANGED':
      return onColumnStateChanged(state, action);
    default:
      return state;
  }
}

function onColumnDefinitionReceived(
  state: SecondaryMarketTradeBlotterState,
  { columnDefinition }: SecondaryMarketTradeBlotterColumnDefinitionReceived,
): SecondaryMarketTradeBlotterState {
  return { ...state, columnDefinition };
}

function onFiltersLoaded(
  state: SecondaryMarketTradeBlotterState,
  { filters }: SecondaryMarketTradeBlotterFiltersLoaded,
): SecondaryMarketTradeBlotterState {
  return { ...state, ...filters };
}

function onPageChanged(
  state: SecondaryMarketTradeBlotterState,
  { page }: SecondaryMarketTradeBlotterPageChanged,
): SecondaryMarketTradeBlotterState {
  return { ...state, page };
}

function onItemsPerPageChanged(
  state: SecondaryMarketTradeBlotterState,
  { itemsPerPage }: SecondaryMarketTradeBlotterItemsPerPageChanged,
): SecondaryMarketTradeBlotterState {
  const blotterPreferenceState = {
    columnState: state.blotterPreferenceState!.columnState,
    pageSizeState: itemsPerPage,
  };
  return { ...state, blotterPreferenceState, page: 1 };
}

function onFilterChanged(
  state: SecondaryMarketTradeBlotterState,
  { filter, value }: SecondaryMarketTradeBlotterFilterChanged,
): SecondaryMarketTradeBlotterState {
  switch (filter) {
    case 'dateFilter': {
      const dateFilter = <DateFilter>value;
      const [customStartDateFilter, customEndDateFilter] = dateFilterToDates(dateFilter);
      return { ...state, dateFilter, customStartDateFilter, customEndDateFilter, page: 1 };
    }
    case 'customStartDateFilter':
    case 'customEndDateFilter':
      return { ...state, dateFilter: 'custom', [filter]: value };
    default:
      return { ...state, [filter]: value, page: 1 };
  }
}

function onAdvancedFilterToggled(state: SecondaryMarketTradeBlotterState): SecondaryMarketTradeBlotterState {
  return { ...state, advancedFilters: !state.advancedFilters, page: 1 };
}

function onItemsReceived(
  state: SecondaryMarketTradeBlotterState,
  { items, totalItems, totalPages }: SecondaryMarketTradeBlotterItemsReceived,
): SecondaryMarketTradeBlotterState {
  const newState = { ...state, totalItems, totalPages, loading: false };
  if (isEqual(state.items, items)) {
    return newState;
  }
  return { ...newState, items };
}

function onItemsRequested(state: SecondaryMarketTradeBlotterState): SecondaryMarketTradeBlotterState {
  return { ...state, loading: true };
}

function dateFilterToDates(dateFilter: DateFilter): [Date, Date] {
  return [toUtcDate(dateFilter), toUtcEndOfDay(now())];
}

function onBlotterPreferenceStateLoaded(
  state: SecondaryMarketTradeBlotterState,
  { blotterPreferenceState }: SecondaryMarketTradeBlotterPreferenceStateLoaded,
): SecondaryMarketTradeBlotterState {
  if (blotterPreferenceState.columnState === undefined) {
    return {
      ...state,
      blotterPreferenceState,
      areBlotterPreferencesInError: false,
      canSaveBlotterPreferences: true,
    };
  }
  const isAllHidden = blotterPreferenceState.columnState.every(i => (i.hide ? i.hide : false));
  return {
    ...state,
    areBlotterPreferencesInError: false,
    canSaveBlotterPreferences: true,
    blotterPreferenceState: isAllHidden
      ? {
          ...blotterPreferenceState,
          columnState: blotterPreferenceState.columnState.map(i => ({
            ...i,
            hide: false,
          })),
        }
      : blotterPreferenceState,
  };
}
